/* eslint-disable consistent-return */
import { createAsyncThunk } from "@reduxjs/toolkit";
import Axios from "axios";
import { getCatalogApiErrorMessage } from "utils/catalogApiErrors";
import { toast } from "utils/toast";

import { setIsSpinnerVisible } from "store/modules/AppConfig/index.store";

import CatalogApi from "services/CatalogApi";

import {
  CoverageAreasResponse,
  UpdateWorkingAreaResponse,
  UpdateWorkingAreaRequest,
  CoverageAreasRequest,
  CoverageCatalogProductsResponse,
  CoverageCatalogCategoriesResponse,
  CoverageCatalogBrandsResponse,
  AddProductRequest,
  AddCatalogRequest,
  UpdateCatalogRequest,
  UpdateProductRequest,
} from "./types.store";

export const handleGetCatalogAreaRequest = createAsyncThunk<
  CoverageAreasResponse,
  CoverageAreasRequest
>("getCatalogAreas", async (data, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));
    const response = await CatalogApi.get(
      `/v1/dealers/${data.userId}/coverage-areas/${data.coverageAreaId}`,
    );

    return response.data;
  } catch (err) {
    toast("Falha ao carregar o catálogo de preços, tente novamente!", {
      type: "error",
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleUpdateCatalogAreaRequest = createAsyncThunk<
  UpdateWorkingAreaResponse,
  { data: UpdateWorkingAreaRequest[]; id: string; userId: string }
>("putCatalogArea", async (request, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    const { data } = await CatalogApi.put(
      `/v1/dealers/${request.userId}/coverage-areas/${request.id}`,
      { productPrices: request.data },
    );
    await dispatch(
      handleGetCatalogAreaRequest({
        coverageAreaId: request.id,
        userId: request.userId,
      }),
    );

    return data;
  } catch (err) {
    const fallback =
      "Falha ao atualizar o catálogo de preços, tente novamente!";

    if (Axios.isAxiosError(err) && err.response) {
      toast.error(getCatalogApiErrorMessage(err.response.data, fallback));
      return rejectWithValue(err.response.data);
    }

    toast.error(fallback);
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleGetCatalogProductsRequest =
  createAsyncThunk<CoverageCatalogProductsResponse>(
    "getCatalogProducts",
    async (_, { rejectWithValue, dispatch }) => {
      try {
        dispatch(setIsSpinnerVisible(true));
        const response = await CatalogApi.get(`/v1/products`);

        return response.data;
      } catch (err) {
        toast("Falha ao carregar o catálogo de produtos, tente novamente!", {
          type: "error",
        });

        if (Axios.isAxiosError(err) && err && err.response) {
          return rejectWithValue(err.response.data);
        }
        return rejectWithValue(err);
      } finally {
        dispatch(setIsSpinnerVisible(false));
      }
    },
  );

export const handleGetCatalogCategoriesRequest =
  createAsyncThunk<CoverageCatalogCategoriesResponse>(
    "getCategoriesProducts",
    async (_, { rejectWithValue, dispatch }) => {
      try {
        dispatch(setIsSpinnerVisible(true));
        const response = await CatalogApi.get(`/v1/categories`);

        return response.data;
      } catch (err) {
        toast("Falha ao carregar o catálogo de categorias, tente novamente!", {
          type: "error",
        });

        if (Axios.isAxiosError(err) && err && err.response) {
          return rejectWithValue(err.response.data);
        }
        return rejectWithValue(err);
      } finally {
        dispatch(setIsSpinnerVisible(false));
      }
    },
  );

export const handleGetCatalogBrandsRequest =
  createAsyncThunk<CoverageCatalogBrandsResponse>(
    "getBrands",
    async (_, { rejectWithValue, dispatch }) => {
      try {
        dispatch(setIsSpinnerVisible(true));
        const response = await CatalogApi.get(`/v1/brands`);

        return response.data;
      } catch (err) {
        toast("Falha ao carregar o catálogo de marcas, tente novamente!", {
          type: "error",
        });

        if (Axios.isAxiosError(err) && err && err.response) {
          return rejectWithValue(err.response.data);
        }
        return rejectWithValue(err);
      } finally {
        dispatch(setIsSpinnerVisible(false));
      }
    },
  );

export const handleAddCatalogProductsRequest = createAsyncThunk<
  any,
  { data: AddProductRequest }
>("putAddProduct", async (request, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    const { data } = await CatalogApi.post(`/v1/products`, request.data);
    await dispatch(handleGetCatalogProductsRequest());

    return data;
  } catch (err) {
    toast("Falha ao adicionar produto, tente novamente!", {
      type: "error",
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleAddCatalogBrandRequest = createAsyncThunk<
  any,
  { data: AddCatalogRequest }
>("postAddBrand", async (request, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    const { data } = await CatalogApi.post(`/v1/brands`, request.data);

    await dispatch(handleGetCatalogBrandsRequest());

    return data;
  } catch (err) {
    toast("Falha ao adicionar marca, tente novamente!", {
      type: "error",
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleAddCatalogCategorieRequest = createAsyncThunk<
  any,
  { data: AddCatalogRequest }
>("postAddCategorie", async (request, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    const { data } = await CatalogApi.post(`/v1/categories`, request.data);

    await dispatch(handleGetCatalogCategoriesRequest());

    return data;
  } catch (err) {
    toast("Falha ao adicionar categoria, tente novamente!", {
      type: "error",
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleUpdateCategorieRequest = createAsyncThunk<
  any,
  { data: UpdateCatalogRequest; id: string }
>("putCatalogCategorie", async (request, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    const { data } = await CatalogApi.put(
      `v1/categories/${request.id}`,
      request.data,
    );

    await dispatch(handleGetCatalogCategoriesRequest());

    return data;
  } catch (err) {
    toast("Falha ao atualizar categoria, tente novamente!", {
      type: "error",
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleUpdateBrandRequest = createAsyncThunk<
  any,
  { data: UpdateCatalogRequest; id: string }
>("putCatalogBrand", async (request, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    const { data } = await CatalogApi.put(
      `v1/brands/${request.id}`,
      request.data,
    );

    await dispatch(handleGetCatalogBrandsRequest());
    return data;
  } catch (err) {
    toast("Falha ao atualizar marca, tente novamente!", {
      type: "error",
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleUpdateProductRequest = createAsyncThunk<
  any,
  { data: UpdateProductRequest; id: string }
>("putCatalogBrand", async (request, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    const { data } = await CatalogApi.put(
      `v1/products/${request.id}`,
      request.data,
    );

    await dispatch(handleGetCatalogProductsRequest());

    return data;
  } catch (err) {
    toast("Falha ao atualizar os produtos, tente novamente!", {
      type: "error",
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleDeleteProductRequest = createAsyncThunk<
  void,
  { productId: string }
>(
  "deleteCatalogProduct",
  async ({ productId }, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setIsSpinnerVisible(true));

      await CatalogApi.delete(`v1/products/${productId}`);

      await dispatch(handleGetCatalogProductsRequest());
    } catch (err) {
      toast("Falha ao deletar produto, tente novamente!", {
        type: "error",
      });

      if (Axios.isAxiosError(err) && err && err.response) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue(err);
    } finally {
      dispatch(setIsSpinnerVisible(false));
    }
  },
);

export const handleDeleteCategoryRequest = createAsyncThunk<
  void,
  { categoryId: string }
>(
  "deleteCatalogCategory",
  async ({ categoryId }, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setIsSpinnerVisible(true));

      await CatalogApi.delete(`v1/categories/${categoryId}`);

      await dispatch(handleGetCatalogCategoriesRequest());
    } catch (err) {
      toast("Falha ao deletar categoria, tente novamente!", {
        type: "error",
      });

      if (Axios.isAxiosError(err) && err && err.response) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue(err);
    } finally {
      dispatch(setIsSpinnerVisible(false));
    }
  },
);

export const handleDeleteBrandRequest = createAsyncThunk<
  void,
  { brandId: string }
>("deleteCatalogBrand", async ({ brandId }, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    await CatalogApi.delete(`v1/brands/${brandId}`);

    await dispatch(handleGetCatalogBrandsRequest());
  } catch (err) {
    toast("Falha ao deletar marca, tente novamente!", {
      type: "error",
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});
